<script setup lang="ts">
    import { VerificationStepStatus, type VerificationStep } from '~/stores/aiAssistantTypes';

    const props = defineProps<{
        documentId: string;
        documentType: string;
        steps: VerificationStep[];
        isComplete: boolean;
        finalStatus: 'pass' | 'issues_found' | null;
        minimized?: boolean;
        autoFixing?: boolean;
    }>();

    const { t } = useTranslation();

    const emit = defineEmits<{
        dismiss: [documentId: string];
        toggleMinimize: [documentId: string];
    }>();

    const KIND_META: Record<string, { label: string }> = {
        quote: { label: 'Devis' },
        invoice: { label: 'Facture' },
        'credit-note': { label: 'Avoir' },
        'order-slip': { label: 'Bon de commande' },
        'delivery-slip': { label: 'Bon de livraison' },
    };

    const totalSteps = computed(() => props.steps[0]?.total ?? 0);

    const completedCount = computed(() =>
        props.steps.filter(s => s.status !== VerificationStepStatus.Running).length,
    );

    const errorCount = computed(() =>
        props.steps.filter(s => s.status === VerificationStepStatus.Issue).length,
    );

    const warningCount = computed(() =>
        props.steps.filter(s => s.status === VerificationStepStatus.Warning).length,
    );

    // Warnings alone are not errors — the pill turns red only for real Issue steps.
    const hasErrors = computed(() => errorCount.value > 0);
    const hasWarningsOnly = computed(() => !hasErrors.value && warningCount.value > 0);

    const kindLabel = computed(() => KIND_META[props.documentType]?.label ?? 'Document');

    const progressTitle = computed(() => t('ai.chat.verification.title_progress', { kind: kindLabel.value.toLowerCase() }));
    const completedTitle = computed(() => t('ai.chat.verification.title_completed', { kind: kindLabel.value.toLowerCase() }));

    const minimizedLabel = computed(() => {
        if (!props.isComplete) return `${completedCount.value}/${totalSteps.value}`;

        const parts: string[] = [];
        if (errorCount.value > 0) {
            const word = errorCount.value > 1 ? t('ai.chat.verification.errors') : t('ai.chat.verification.error');
            parts.push(`${errorCount.value} ${word}`);
        }
        if (warningCount.value > 0) {
            const word = warningCount.value > 1 ? t('ai.chat.verification.warnings') : t('ai.chat.verification.warning');
            parts.push(`${warningCount.value} ${word}`);
        }

        return parts.length ? parts.join(', ') : t('ai.chat.verification.ok');
    });

    const handleDismiss = () => {
        emit('dismiss', props.documentId);
    };

    const handleToggleMinimize = () => {
        emit('toggleMinimize', props.documentId);
    };

    const failedSubSteps = (step: VerificationStep) =>
        step.subSteps?.filter(s => s.status === VerificationStepStatus.Warning || s.status === VerificationStepStatus.Issue) ?? [];
</script>

<template>
    <!-- Minimized pill -->
    <button
        v-if="minimized"
        type="button"
        class="tpv-verify-pill"
        :class="{
            'tpv-verify-pill--pass': isComplete && !hasErrors && !hasWarningsOnly,
            'tpv-verify-pill--warning': isComplete && hasWarningsOnly,
            'tpv-verify-pill--issues': isComplete && hasErrors,
            'tpv-verify-pill--running': !isComplete,
        }"
        @click="handleToggleMinimize"
    >
        <span
            v-if="!isComplete"
            class="tpv-spinner tpv-spinner--pill"
        />
        <i
            v-else-if="hasErrors"
            class="fa-solid fa-circle-exclamation tpv-pill-icon tpv-pill-icon--issues"
        />
        <i
            v-else-if="hasWarningsOnly"
            class="fa-solid fa-triangle-exclamation tpv-pill-icon tpv-pill-icon--warning"
        />
        <i
            v-else
            class="fa-solid fa-circle-check tpv-pill-icon tpv-pill-icon--pass"
        />
        <span class="tpv-pill-label">{{ minimizedLabel }}</span>
        <i class="fa-solid fa-chevron-up tpv-pill-expand" />
    </button>

    <!-- Expanded card -->
    <div
        v-else
        class="tpv-verify-card"
    >
        <div class="tpv-verify-header">
            <span
                v-if="!isComplete"
                class="tpv-spinner tpv-spinner--header"
            />
            <i
                v-else-if="hasErrors"
                class="fa-solid fa-circle-exclamation tpv-header-warn"
            />
            <i
                v-else-if="hasWarningsOnly"
                class="fa-solid fa-triangle-exclamation tpv-header-warning"
            />
            <i
                v-else
                class="fa-solid fa-circle-check tpv-header-check"
            />

            <span class="tpv-verify-title">
                {{ isComplete ? completedTitle : progressTitle }}
            </span>

            <span class="tpv-verify-counter">
                {{ completedCount }}/{{ totalSteps }}
            </span>

            <button
                type="button"
                class="tpv-verify-btn"
                title="Minimiser"
                aria-label="Minimiser"
                @click="handleToggleMinimize"
            >
                <i class="fa-solid fa-chevron-down" />
            </button>

            <button
                type="button"
                class="tpv-verify-btn"
                title="Fermer"
                aria-label="Fermer"
                @click="handleDismiss"
            >
                <i class="fa-solid fa-xmark" />
            </button>
        </div>

        <ol class="tpv-verify-steps">
            <li
                v-for="step in steps"
                :key="step.step"
                class="tpv-verify-step"
                :class="`tpv-verify-step--${step.status}`"
            >
                <span class="tpv-step-indicator">
                    <span
                        v-if="step.status === VerificationStepStatus.Running"
                        class="tpv-spinner tpv-spinner--step"
                    />
                    <i
                        v-else-if="step.status === VerificationStepStatus.Pass"
                        class="fa-solid fa-check tpv-step-icon"
                    />
                    <i
                        v-else-if="step.status === VerificationStepStatus.Warning"
                        class="fa-solid fa-exclamation tpv-step-icon"
                    />
                    <i
                        v-else-if="step.status === VerificationStepStatus.Issue"
                        class="fa-solid fa-xmark tpv-step-icon"
                    />
                    <span
                        v-else
                        class="tpv-step-dot"
                    />
                </span>
                <span class="tpv-step-label">
                    {{ step.label }}
                    <span
                        v-if="!step.subSteps?.length && (step.status === VerificationStepStatus.Issue || step.status === VerificationStepStatus.Warning) && step.message"
                        class="tpv-step-message"
                        :class="{ 'tpv-step-message--warning': step.status === VerificationStepStatus.Warning }"
                    >{{ step.message }}</span>

                    <ol
                        v-if="failedSubSteps(step).length"
                        class="tpv-sub-steps"
                    >
                        <li
                            v-for="(sub, idx) in failedSubSteps(step)"
                            :key="idx"
                            class="tpv-sub-step"
                            :class="`tpv-sub-step--${sub.status}`"
                        >
                            <span class="tpv-sub-indicator">
                                <i v-if="sub.status === VerificationStepStatus.Warning" class="fa-solid fa-exclamation tpv-sub-icon" />
                                <i v-else class="fa-solid fa-xmark tpv-sub-icon" />
                            </span>
                            <span class="tpv-sub-label">
                                {{ sub.label }}
                                <span
                                    v-if="sub.message"
                                    class="tpv-step-message"
                                    :class="{ 'tpv-step-message--warning': sub.status === VerificationStepStatus.Warning }"
                                >{{ sub.message }}</span>
                            </span>
                        </li>
                    </ol>
                </span>
            </li>
        </ol>

        <!-- Auto-fix in progress — the agent is correcting the document after the run. -->
        <div
            v-if="autoFixing"
            class="tpv-autofix-line"
        >
            <i class="fa-solid fa-wand-magic-sparkles tpv-autofix-icon" />
            <span>{{ t('ai.chat.verification.auto_fixing') }}</span>
        </div>
    </div>
</template>

<style scoped>
/* ─── Auto-fix progress line ─── */
.tpv-autofix-line {
    display: flex;
    align-items: center;
    gap: 7px;
    margin-top: 8px;
    padding: 8px 14px 10px;
    font-size: 11.5px;
    color: #b07503;
    animation: tpv-autofix-pulse 1.4s ease-in-out infinite;
}

.tpv-autofix-icon {
    font-size: 11px;
    color: #e8b102;
}

@keyframes tpv-autofix-pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.45; }
}

/* ─── Minimized pill ─── */
.tpv-verify-pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 5px 12px;
    border-radius: 9999px;
    border: 1px solid #e5e5e5;
    background: #fff;
    font-size: 11.5px;
    font-weight: 600;
    color: #42484e;
    cursor: pointer;
    transition: background 0.12s, border-color 0.12s;
    animation: tpv-fade-in 0.2s ease-out;
}

.tpv-verify-pill:hover {
    background: #f8f8f8;
    border-color: #ccc;
}

.tpv-verify-pill--pass {
    border-color: #b6e0c5;
    background: #f0faf4;
}

.tpv-verify-pill--pass:hover {
    background: #e2f5ea;
}

.tpv-verify-pill--issues {
    border-color: #fdc6c6;
    background: #fef8f8;
}

.tpv-verify-pill--issues:hover {
    background: #fef2f2;
}

.tpv-verify-pill--warning {
    border-color: #f5e0a3;
    background: #fefbf2;
}

.tpv-verify-pill--warning:hover {
    background: #fdf6e3;
}

.tpv-pill-icon {
    font-size: 12px;
}

.tpv-pill-icon--pass {
    color: #1a8754;
}

.tpv-pill-icon--issues {
    color: #dc2626;
}

.tpv-pill-icon--warning {
    color: #d99304;
}

.tpv-pill-label {
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 10.5px;
}

.tpv-pill-expand {
    font-size: 8px;
    color: #999;
    margin-left: 2px;
}

.tpv-spinner--pill {
    width: 10px;
    height: 10px;
    border-width: 2px;
}

/* ─── Expanded card ─── */
.tpv-verify-card {
    background: #fff;
    border: 1px solid #e5e5e5;
    border-radius: 12px;
    box-shadow: 0 1px 2px rgba(15, 16, 32, 0.04);
    width: 100%;
    overflow: hidden;
    animation: tpv-fade-in 0.25s ease-out;
}

.tpv-verify-header {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 14px;
    border-bottom: 1px solid #e5e5e5;
    font-size: 12.5px;
    font-weight: 600;
    color: #0f1020;
}

.tpv-verify-title {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.tpv-verify-counter {
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 10px;
    font-weight: 600;
    color: #42484e;
    background: #fafafa;
    border: 1px solid #e5e5e5;
    border-radius: 9999px;
    padding: 3px 8px;
}

.tpv-verify-btn {
    width: 22px;
    height: 22px;
    border-radius: 6px;
    border: none;
    background: transparent;
    color: #888;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    font-size: 12px;
    transition: background 0.12s, color 0.12s;
}

.tpv-verify-btn:hover {
    background: #f2f2f2;
    color: #0f1020;
}

.tpv-verify-btn:focus-visible {
    outline: 2px solid #0f1020;
    outline-offset: 2px;
}

.tpv-header-check {
    font-size: 14px;
    color: #1a8754;
}

.tpv-header-warn {
    font-size: 14px;
    color: #dc2626;
}

.tpv-header-warning {
    font-size: 14px;
    color: #d99304;
}

.tpv-verify-steps {
    list-style: none;
    margin: 0;
    padding: 12px 14px 14px;
    display: flex;
    flex-direction: column;
}

.tpv-verify-step {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 5px 0;
    font-size: 12.5px;
    color: #b5b5b5;
    animation: tpv-fade-in 0.25s ease-out;
}

.tpv-verify-step .tpv-step-indicator {
    margin-top: 2px;
}

.tpv-verify-step--running {
    color: #0f1020;
    font-weight: 500;
}

.tpv-verify-step--pass,
.tpv-verify-step--skipped {
    color: #42484e;
}

.tpv-verify-step--warning {
    color: #0f1020;
}

.tpv-verify-step--issue {
    color: #0f1020;
}

.tpv-step-indicator {
    width: 16px;
    height: 16px;
    border-radius: 9999px;
    flex: none;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: #fafafa;
    border: 1px solid #e5e5e5;
}

.tpv-verify-step--running .tpv-step-indicator {
    background: #fef8ea;
    border-color: #e8b102;
}

.tpv-verify-step--pass .tpv-step-indicator {
    background: #e8f6ee;
    border-color: #b6e0c5;
}

.tpv-verify-step--warning .tpv-step-indicator {
    background: #fef8ea;
    border-color: #e8b102;
}

.tpv-verify-step--issue .tpv-step-indicator {
    background: #fef2f2;
    border-color: #fdc6c6;
}

.tpv-step-icon {
    font-size: 8px;
}

.tpv-verify-step--pass .tpv-step-icon {
    color: #1a8754;
}

.tpv-verify-step--warning .tpv-step-icon {
    color: #e8b102;
}

.tpv-verify-step--issue .tpv-step-icon {
    color: #dc2626;
}

.tpv-step-dot {
    width: 4px;
    height: 4px;
    border-radius: 9999px;
    background: #b5b5b5;
}

.tpv-step-label {
    flex: 1;
    min-width: 0;
    display: inline-flex;
    flex-direction: column;
    gap: 2px;
}

.tpv-step-message {
    font-size: 11.5px;
    font-weight: 400;
    color: #dc2626;
}

.tpv-step-message--warning {
    color: #b45309;
}

.tpv-spinner {
    display: inline-block;
    border-radius: 50%;
    border: 2px solid #e5e5e5;
    border-top-color: #e8b102;
    animation: tpv-spin 0.8s linear infinite;
    flex: none;
}

.tpv-spinner--header {
    width: 14px;
    height: 14px;
}

.tpv-spinner--step {
    width: 8px;
    height: 8px;
    border-width: 2px;
}

@keyframes tpv-spin {
    to { transform: rotate(360deg); }
}

@keyframes tpv-fade-in {
    from { opacity: 0; transform: translateY(2px); }
    to { opacity: 1; transform: none; }
}

/* --- Sub-steps (failed only) --- */
.tpv-sub-steps {
    list-style: none;
    margin: 4px 0 0;
    padding: 0 0 0 6px;
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.tpv-sub-step {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    font-size: 11.5px;
    color: #666;
    animation: tpv-fade-in 0.15s ease-out;
}

.tpv-sub-step .tpv-sub-indicator {
    margin-top: 2px;
}

.tpv-sub-step--warning { color: #0f1020; }
.tpv-sub-step--issue { color: #0f1020; }

.tpv-sub-indicator {
    width: 12px;
    height: 12px;
    border-radius: 9999px;
    flex: none;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: #fafafa;
    border: 1px solid #e5e5e5;
}

.tpv-sub-step--warning .tpv-sub-indicator { background: #fef8ea; border-color: #e8b102; }
.tpv-sub-step--issue .tpv-sub-indicator { background: #fef2f2; border-color: #fdc6c6; }

.tpv-sub-icon { font-size: 6px; }
.tpv-sub-step--warning .tpv-sub-icon { color: #e8b102; }
.tpv-sub-step--issue .tpv-sub-icon { color: #dc2626; }

.tpv-sub-label {
    flex: 1;
    min-width: 0;
    display: inline-flex;
    flex-direction: column;
    gap: 1px;
}
</style>
